<template>
    <section id="services" class="services-section">
        <div class="container">
            <div class="section-identifier">
                {{ t.services.section }}
            </div>

            <div class="services-heading">
                <div>
                    <p class="eyebrow">
                        <span></span>
                        {{ t.services.eyebrow }}
                    </p>

                    <h2>
                        {{ t.services.titleFirst }}
                        <em>{{ t.services.titleSecond }}</em>
                    </h2>
                </div>

                <p class="services-description">
                    {{ t.services.description }}
                </p>
            </div>

            <div class="services-grid">
                <article v-for="(service, index) in t.services.items"
                         :key="service.title"
                         class="service-card">
                    <div class="card-header">
                        <span class="service-number">
                            {{ String(index + 1).padStart(2, '0') }}
                        </span>

                        <div class="service-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24">
                                <path v-if="index === 0"
                                      d="M3 20 8 4h8l5 16M7 15h10M9 10h6" />

                                <path v-else-if="index === 1"
                                      d="M4 19V9l8-5 8 5v10M8 19v-6h8v6M2 19h20" />

                                <path v-else-if="index === 2"
                                      d="M4 20 8 4h8l4 16M8 16h8M9 11h6" />

                                <path v-else-if="index === 3"
                                      d="M4 7 12 3l8 4-8 4-8-4ZM4 12l8 4 8-4M4 17l8 4 8-4" />

                                <path v-else-if="index === 4"
                                      d="M3 20V8l9-5 9 5v12M7 20v-7h10v7M8 9h8" />

                                <path v-else
                                      d="M12 3 4 7v10l8 4 8-4V7l-8-4ZM4 7l8 4 8-4M12 11v10" />
                            </svg>
                        </div>
                    </div>

                    <h3>{{ service.title }}</h3>

                    <p>{{ service.description }}</p>

                    <a href="#contact"
                       @click="selectService(index)">
                        {{ t.services.action }}

                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M5 12h14"></path>
                            <path d="m13 6 6 6-6 6"></path>
                        </svg>
                    </a>
                </article>
            </div>
        </div>
    </section>
</template>

<script>
export default {
  name: 'ServicesSection',

  props: {
    t: {
      type: Object,
      required: true
    }
  },

  emits: ['select-service'],

  methods: {
    selectService(index) {
      this.$emit('select-service', index)
    }
  }
}
</script>

<style scoped>
    .services-section {
        padding-block: var(--section-padding);
        background-color: var(--color-concrete);
    }

    .section-identifier {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 62px;
        color: #77736c;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.2em;
        text-transform: uppercase;
    }

        .section-identifier span {
            display: grid;
            width: 33px;
            height: 33px;
            place-items: center;
            color: var(--color-orange-dark);
            border: 1px solid var(--color-border);
            font-size: 9px;
        }

    .services-heading {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 60px;
        margin-bottom: 70px;
    }

    .eyebrow {
        display: flex;
        align-items: center;
        gap: 13px;
        margin-bottom: 25px;
        color: var(--color-orange-dark);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.21em;
        text-transform: uppercase;
    }

        .eyebrow span {
            width: 32px;
            height: 2px;
            background-color: currentColor;
        }

    h2 {
        max-width: 850px;
        margin: 0;
        color: var(--color-charcoal);
        font-family: var(--font-heading);
        font-size: clamp(50px, 5.3vw, 82px);
        font-weight: 400;
        letter-spacing: -0.045em;
        line-height: 0.98;
    }

        h2 em {
            display: block;
            color: var(--color-orange);
            font-weight: 400;
        }

    .services-description {
        max-width: 390px;
        margin: 0 0 5px;
        color: var(--color-muted);
        font-size: 16px;
        line-height: 1.75;
    }

    .services-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        border-top: 1px solid var(--color-border);
        border-left: 1px solid var(--color-border);
    }

    .service-card {
        display: flex;
        min-height: 350px;
        padding: 38px;
        flex-direction: column;
        border-right: 1px solid var(--color-border);
        border-bottom: 1px solid var(--color-border);
        transition: background-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
    }

        .service-card:hover {
            z-index: 2;
            background-color: #e9e4da;
            box-shadow: 0 20px 45px rgba(36, 35, 31, 0.1);
            transform: translateY(-4px);
        }

    .card-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }

    .service-number {
        color: var(--color-orange-dark);
        font-size: 10px;
        font-weight: 800;
    }

    .service-icon {
        display: grid;
        width: 45px;
        height: 45px;
        place-items: center;
        color: var(--color-orange-dark);
        border: 1px solid rgba(189, 82, 25, 0.35);
    }

        .service-icon svg {
            width: 23px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.45;
        }

    h3 {
        margin: 40px 0 16px;
        color: var(--color-charcoal);
        font-family: var(--font-heading);
        font-size: 30px;
        font-weight: 400;
        line-height: 1.1;
    }

    .service-card p {
        margin: 0;
        color: var(--color-muted);
        font-size: 14px;
        line-height: 1.7;
    }

    .service-card a {
        display: inline-flex;
        align-items: center;
        align-self: flex-start;
        gap: 11px;
        margin-top: auto;
        padding-top: 28px;
        color: var(--color-charcoal);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
    }

        .service-card a svg {
            width: 17px;
            fill: none;
            stroke: var(--color-orange-dark);
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.8;
            transition: transform 0.2s ease;
        }

        .service-card a:hover svg {
            transform: translateX(4px);
        }

    @media (max-width: 900px) {
        .services-heading {
            display: block;
        }

        .services-description {
            margin-top: 30px;
        }

        .services-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 580px) {
        .section-identifier {
            margin-bottom: 40px;
        }

        .services-heading {
            margin-bottom: 50px;
        }

        .services-grid {
            grid-template-columns: 1fr;
        }

        .service-card {
            min-height: 310px;
            padding: 30px;
        }
    }
</style>